import React from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout/Layout";
import Section from "../components/section/Section";
import ClinicHeader from "../components/clinic/ClinicHeader";
import ClinicDetails from "../components/clinic/ClinicDetails";
import ClinicBookingForm from "../components/clinic/ClinicBookingform";
import Grid from "../components/grid/Grid";
import ArticleCard from "../components/article-card/ArticleCard";
import TeamMemberThumbnail from "../components/team-member-thumbnail/TeamMemberThumbnail";
import FeatureSection from "../components/feature-section/FeatureSection";
import ClinicRateCard from "../components/clinic/ClinicRateCard";
import ButtonLink from "../components/links/ButtonLink";
import cssClass from "./clinic.module.scss";

const Clinic = ({ data }) => {
  const {
    slug,
    introduction,
    about,
    aboutImage,
    team,
    articles,
  } = data.contentfulClinic;

  const {
    introductionTitle,
    meetClinicTitle,
    teamTitle,
    ratesTitle,
    servicesTitle,
    bookingCallToActionTitle,
    requestInformationLink,
    bookAppointmentLink,
    getDirectionsLink,
  } = data.contentfulClinicPage;

  return (
    <Layout navInvert>
      <ClinicHeader
        {...data.contentfulClinic}
        bookAppointmentLink={bookAppointmentLink}
        getDirectionsLink={getDirectionsLink}
      />

      <Section className={cssClass.approachWrapperOuter}>
        <Grid className={cssClass.approachWrapper}>
          <div className={cssClass.approachText}>
            <h2 className={cssClass.approachTitle}>{introductionTitle}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: introduction.childMarkdownRemark.html,
              }}
            />
          </div>
          <div className={cssClass.approachTeamMember}>
            <TeamMemberThumbnail
              key={team[0].id}
              {...team[0]}
              clinicSlug={slug}
              featured
            />
          </div>
        </Grid>
      </Section>

      <FeatureSection
        image={aboutImage}
        backgroundColor="var(--color-turquoise)"
      >
        <h1>{teamTitle}</h1>
        <div
          dangerouslySetInnerHTML={{ __html: about.childMarkdownRemark.html }}
          className={cssClass.clinicIntroText}
        />
      </FeatureSection>

      {team && (
        <Section verticalPadding>
          <h2 className={cssClass.teamTitle}>{meetClinicTitle}</h2>
          <Grid horizontallyCenterItems columns={4}>
            {team.map((teamMember) => {
              return (
                <TeamMemberThumbnail
                  key={teamMember.id}
                  {...teamMember}
                  clinicSlug={slug}
                />
              );
            })}
          </Grid>
          {/* TODO: SHOW MORE */}
        </Section>
      )}

      <Section verticalPadding className={cssClass.detailsAndFormContainer}>
        <Grid className={cssClass.gridContainer}>
          <ClinicDetails
            className={cssClass.detailsContainer}
            image={data.contentfulClinic.image}
            {...data.contentfulClinic}
          />
          <aside className={cssClass.formContainer}>
            <div className={cssClass.formStickyInnerContainer}>
              <ClinicBookingForm />{" "}
              {/* TODO: reposition into detailsContainer on mobile */}
            </div>
          </aside>
        </Grid>
        <div className={cssClass.detailsAndFormContainerBackground} />
      </Section>

      <div className={cssClass.ourRatesBackground}>
        <Section className={cssClass.ourRatesSection} verticalPadding>
          <h2>{ratesTitle}</h2>
          <Grid columns={4}>
            {/* TODO: replace tests with contentful */}
            <ClinicRateCard
              title="IVF Genetic"
              priceFrom="7995E"
              detailsArray={[
                "Pick up (Egg collection)",
                "A.R. lab proceedings",
                "ICSI (sperm microinjection)/IVF",
                "Extended culture of preembryos (D5)",
                "Preembryo biopsy on Day 5",
                "Pre-embryo vitrification",
                "Genetic sequencing analysis NGS",
                "Pre-embryo warming",
                "Pre-embryo transfer",
              ]}
            />
            <ClinicRateCard
              title="Artificial Insemination"
              priceFrom="840E"
              detailsArray={["Intra Uterine Insemination"]}
            />
            <ClinicRateCard
              title="Preservation of Fertility"
              priceFrom="1980E"
              detailsArray={[
                "Pick up (Egg collection)",
                "Egg vitrification",
                "Consultation",
              ]}
            />
            {/* END TODO */}
          </Grid>
        </Section>
      </div>

      {articles && (
        <Section verticalPadding>
          <h2 className={cssClass.articlesTitle}>{servicesTitle}</h2>
          <Grid articleLayout>
            {articles.map((article, i) => {
              return (
                <ArticleCard
                  isRowLayout={i > 1}
                  key={article.id}
                  {...article}
                />
              );
            })}
          </Grid>
        </Section>
      )}

      <div className={cssClass.bookingCTABackground}>
        <Section className={cssClass.bookingCTA}>
          <div className={cssClass.ctaRowContainer}>
            <h3>{bookingCallToActionTitle}</h3>
            <div>
              <ButtonLink whiteGhost to="/">
                {requestInformationLink}
              </ButtonLink>
              <ButtonLink alternativeInvert to="/">
                {bookAppointmentLink}
              </ButtonLink>
            </div>
          </div>
        </Section>
      </div>
    </Layout>
  );
};

export default Clinic;

export const query = graphql`
  query($slug: String!, $locale: String) {
    contentfulClinic(slug: { eq: $slug }, node_locale: { eq: $locale }) {
      name
      slug
      phone
      email
      address {
        childMarkdownRemark {
          html
        }
      }
      image {
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      introduction {
        childMarkdownRemark {
          html
        }
      }
      about {
        childMarkdownRemark {
          html
        }
      }
      aboutImage {
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      team {
        id
        name
        title
        image {
          title
          fluid {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        slug
      }
      articles {
        id
        name
        thumbnail {
          title
          fluid {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        slug
        topic {
          name
          slug
        }
      }
      pregnancyRate
      multiplePregnancyRate
      testimonial
      facilities
      services
      spokenLanguages
      insurancePartners {
        id
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
    }
    contentfulClinicPage(node_locale: { eq: $locale }) {
      introductionTitle
      meetClinicTitle
      teamTitle
      ratesTitle
      servicesTitle
      bookingCallToActionTitle
      requestInformationLink
      bookAppointmentLink
      getDirectionsLink
    }
  }
`;
